import React from 'react';
import * as Sentry from '@sentry/react';
import { EmergencyClinicsFragment, useSoftDeleteEmergencyClinicMutation } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';

interface IModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinic: EmergencyClinicsFragment;
}

const DeleteEmergencyContactModal = ({ isOpen, onClose, clinic }: IModalProps): JSX.Element | null => {
  const { currentClinicId } = useClinicUser();
  const toast = useToast();
  const cancelDeleteRef = React.useRef(null);
  const [deleteEmergencyClinic, { loading }] = useSoftDeleteEmergencyClinicMutation();

  const handleDelete = async (): Promise<void> => {
    if (!clinic?.id || !currentClinicId) return;
    try {
      await deleteEmergencyClinic({
        variables: { where: { id: clinic.id } },
      });

      if (!onClose) return;
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      toast({
        status: 'error',
        title: 'Oops, an error occurred while deleting this emergency clinic.',
      });
      console.error(e);
    }
  };

  if (!clinic) {
    return null;
  }

  return (
    <AlertDialog isCentered isOpen={isOpen} leastDestructiveRef={cancelDeleteRef} onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader color="#000b8e">Delete Emergency Clinic</AlertDialogHeader>
          <AlertDialogBody>Are you sure? You cannot undo this action.</AlertDialogBody>
          <AlertDialogFooter>
            <Button variant="tertiary" ref={cancelDeleteRef} onClick={onClose}>
              Cancel
            </Button>
            <Button isLoading={loading} variant="primaryDestructive" onClick={handleDelete} ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default DeleteEmergencyContactModal;
